/* @flow */

import type { NETGEM_API_V8_FEED_ITEM } from '../../libs/netgemLibrary/v8/types/FeedItem';
import type { NETGEM_API_PLAYINGHISTORY, NETGEM_API_PLAYINGHISTORY_ITEM } from '../../libs/netgemLibrary/v8/types/PlayingHistory';
import type { BasicFunction, KeyValuePair } from '../jsHelpers/types';
import type { GamestreamApiResponse, GamestreamToken, PromptSessionCloseType, ShowEulaType, ShowGameProfileSelectType, ShowMessageType, UpdatePlayingHistoryItemType } from './types';

import { HttpStatus } from '../../libs/netgemLibrary/v8/constants/NetworkCodesAndMessages';
import AccurateTimestamp from '../dateTime/AccurateTimestamp';
import { logError } from '../debug/debug';
import { LoadableStatus } from '../loadable/loadable';
import Localizer from '../localization';
import { Messenger, MessengerEvents } from '../messenger';
import GamestreamWrapper from './gamestreamWrapper';
import { copyOrInitPlayingHistoryItem, notifyError } from './helpers';
import { ClientState, GamestreamStatus } from './types';

class GameManager {
  static #CGXClientClass: Class<CGXClient> | null = null;
  static #SGXSessionTrackerClass: Class<SGXSessionTracker> | null = null;
  static #clientState: ClientState = ClientState.NotLoaded;
  static #container: HTMLElement | null = null;
  static #errorMessages: KeyValuePair<Array<{ lang: string, value: string }>> | null = null;
  static #gsWrapper: GamestreamWrapper | null = null;
  static #reopenModalCard: BasicFunction | null = null;
  static #wrapper: HTMLElement | null = null;

  static get container(): HTMLElement | null {
    return this.#container;
  }

  static get isGamingEnabled(): boolean {
    return this.#errorMessages !== null;
  }

  static get CGXClientClass(): Class<CGXClient> {
    if (GameManager.#CGXClientClass === null) {
      throw new Error('CGXClient has not been loaded yet');
    }
    return GameManager.#CGXClientClass;
  }

  static get SGXSessionTrackerClass(): Class<SGXSessionTracker> {
    if (GameManager.#SGXSessionTrackerClass === null) {
      throw new Error('SGXSessionTracker has not been loaded yet');
    }
    return GameManager.#SGXSessionTrackerClass;
  }

  // $FlowFixMe[unsupported-syntax] Flow does not support symbols yet
  get [Symbol.toStringTag]() {
    return 'GameManager';
  }

  /*
   * Static methods
   */

  static loadErrorMessages: (errorMessagesUrl: string) => Promise<void> = async (errorMessagesUrl) => {
    try {
      const response = await fetch(errorMessagesUrl);
      GameManager.#errorMessages = await response.json();
    } catch (error) {
      Messenger.emit(MessengerEvents.NOTIFY_ERROR, Localizer.localize('gaming.errors.error_messages_load_error', { errorMsg: error.message }));
    }
  };

  static getErrorMessagesFromCode: (errorCode: number) => Array<{ lang: string, value: string }> = (errorCode) => {
    if (GameManager.#errorMessages === null) {
      return [];
    }

    return GameManager.#errorMessages[errorCode.toString()] ?? [];
  };

  static launchGame: (launchGameData: {
    avatarBaseUrl: string,
    gameAlias: string,
    getGamestreamToken: (item: NETGEM_API_V8_FEED_ITEM, signal?: AbortSignal) => Promise<GamestreamToken>,
    isGamepadOnly: boolean,
    item: NETGEM_API_V8_FEED_ITEM,
    playingHistory: NETGEM_API_PLAYINGHISTORY,
    playingHistoryStatus: LoadableStatus,
    profileId: string | null,
    reopenModalCard?: BasicFunction,
    showEula: ShowEulaType,
    showGameProfileSelect: ShowGameProfileSelectType,
    showMessage: ShowMessageType,
    promptSessionClose: PromptSessionCloseType,
    updatePlayingHistoryItem: UpdatePlayingHistoryItemType,
  }) => Promise<void> = async (launchGameData): Promise<void> => {
    const {
      avatarBaseUrl,
      gameAlias,
      getGamestreamToken,
      isGamepadOnly,
      item,
      playingHistory,
      playingHistoryStatus,
      profileId,
      promptSessionClose,
      reopenModalCard,
      showEula,
      showGameProfileSelect,
      showMessage,
      updatePlayingHistoryItem,
    } = launchGameData;

    GameManager.#reopenModalCard = reopenModalCard ?? null;

    // Create DIV that will receive the client
    GameManager.createContainer();

    const launchGameInternal = async (): Promise<void> => {
      try {
        let token: string | null = null;
        let url: string | null = null;

        try {
          ({ token, url } = await getGamestreamToken(item));
        } catch (error) {
          logError(error);
          if (error.getStatus?.() === HttpStatus.NotFound) {
            throw { gsStatusCode: GamestreamStatus.NoSubscriptionForGame };
          }
          throw error;
        }

        // Load GS client
        await GameManager.checkLoadClient();

        // Build playing history item for future use
        const playingHistoryItem: NETGEM_API_PLAYINGHISTORY_ITEM | null = copyOrInitPlayingHistoryItem(playingHistoryStatus, playingHistory, item);
        const { selectedProgramId } = item;
        const updatePlayingHistory = async (currentProfileId: string) => {
          if (playingHistoryItem === null) {
            return;
          }
          try {
            playingHistoryItem.profiles[currentProfileId] = { date: AccurateTimestamp.nowAsDate().toISOString() };
            await updatePlayingHistoryItem(playingHistoryItem, selectedProgramId);
          } catch (error) {
            logError(error);
          }
        };

        GameManager.#gsWrapper = new GamestreamWrapper(url, avatarBaseUrl, token, GameManager.handleGameStopped, GameManager.handleGameError, showEula, promptSessionClose, updatePlayingHistory);

        // Select profile (if provided), start session, approve EULA (if necessary), then run game
        await GameManager.#gsWrapper.initialize(gameAlias, showGameProfileSelect, profileId);
      } catch (error) {
        GameManager.handleGameError(error);
      }
    };

    if (isGamepadOnly) {
      Messenger.once(MessengerEvents.MODAL_CLOSED, launchGameInternal);

      showMessage(
        {
          buttonText: Localizer.localize('common.actions.close'),
          message: Localizer.localize('gaming.no_keyboard.message'),
          title: Localizer.localize('gaming.no_keyboard.title'),
        },
        Localizer.localize('accessibility.modal.message_gamepad_required'),
      );
      return;
    }

    await launchGameInternal();
  };

  static checkLoadClient: () => Promise<void> = async () => {
    if (GameManager.#clientState !== ClientState.NotLoaded) {
      return;
    }

    GameManager.#clientState = ClientState.Loading;

    const clientModule = await import('./cgxclient.min.js');
    GameManager.#CGXClientClass = clientModule.default;

    const sessionTrackerModule = await import('./sgxsessiontracker.min.js');
    GameManager.#SGXSessionTrackerClass = sessionTrackerModule.default;

    GameManager.#clientState = ClientState.Loaded;
  };

  static createContainer: () => void = () => {
    const clientContainerElt = document.createElement('div');
    clientContainerElt.className = 'gsClientContainer';

    const containerWrapperElt = document.createElement('div');
    containerWrapperElt.className = 'gsContainerWrapper';

    const loaderElt = document.createElement('div');
    loaderElt.className = 'infiniteCircleLoaderArc';

    const exitElt = document.createElement('button');
    exitElt.className = 'exitButton';
    exitElt.innerHTML = '×';
    exitElt.addEventListener('click', GameManager.exit);

    containerWrapperElt.appendChild(loaderElt);
    containerWrapperElt.appendChild(clientContainerElt);
    containerWrapperElt.appendChild(exitElt);
    document.body.appendChild(containerWrapperElt);

    GameManager.#wrapper = containerWrapperElt;
    GameManager.#container = clientContainerElt;
  };

  static destroyContainer: () => void = () => {
    if (GameManager.#container === null) {
      return;
    }

    GameManager.#wrapper?.remove();
    GameManager.#wrapper = null;
    GameManager.#container = null;
  };

  static exit = () => {
    if (GameManager.#gsWrapper) {
      GameManager.#gsWrapper.exit();
    } else {
      GameManager.handleGameStopped();
    }
  };

  static handleGameStopped = () => {
    GameManager.destroyContainer();
    Messenger.emit(MessengerEvents.REFRESH_PLAYING_HISTORY);

    // Reopen last modal card if any
    GameManager.#reopenModalCard?.();
  };

  static handleGameError = (error: GamestreamApiResponse) => {
    GameManager.destroyContainer();
    logError(error);

    const { gsStatusCode, message: errorMsg } = error;

    if (typeof gsStatusCode === 'number') {
      notifyError(gsStatusCode, errorMsg);
    } else {
      Messenger.emit(MessengerEvents.NOTIFY_ERROR, Localizer.localize('gaming.errors.global_with_message', { errorMsg: errorMsg ?? '' }));
    }
  };
}

export default GameManager;
